import { useEffect, useMemo, useState } from "react";
import UserRow from "./UserRow";
import { PeopleListSkeleton } from '../ui/skeletons';

// Structural subset of ChatLayout's channelVisibleMembersWithPresence entries —
// only the fields the rows read (ground rule 5: no back-imports). The richer
// activity/status/roles fields are accessed via `as any`, mirroring the original
// inline JSX.
type PeopleMember = {
  id: string;
  name?: string | null;
  username?: string | null;
  avatarUrl?: string | null;
  nameColor?: string | null;
  onMobile?: boolean;
  isIdle?: boolean;
};

type SortMode = 'status' | 'alpha' | 'roles';
type Role = { id: string; name: string; color?: string | null; position?: number };

const SORT_OPTIONS: { id: SortMode; label: string }[] = [
  { id: 'status', label: 'Online first' },
  { id: 'alpha', label: 'Alphabetical' },
  { id: 'roles', label: 'By role' },
];

const nameOf = (m: PeopleMember) => (m.name || m.username || '').toString();
const rolesOf = (m: PeopleMember): Role[] => {
  const r = (m as any).roles;
  return Array.isArray(r) ? r : [];
};

function SortMenu({ mode, onChange }: { mode: SortMode; onChange: (m: SortMode) => void }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex h-7 w-7 items-center justify-center rounded-lg text-fg-2 transition hover:bg-neutral-800/60 hover:text-fg active:scale-95"
        title="Sort members"
        aria-label="Sort members"
      >
        <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 6h16M6 12h12M9 18h6" /></svg>
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-8 z-50 w-44 overflow-hidden rounded-xl border border-neutral-800 bg-neutral-950 p-1 shadow-2xl">
            {SORT_OPTIONS.map((o) => (
              <button
                key={o.id}
                onClick={() => { onChange(o.id); setOpen(false); }}
                className={`flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-sm transition ${mode === o.id ? 'text-emerald-300' : 'text-fg-2 hover:bg-neutral-800/50'}`}
              >
                {o.label}
                {mode === o.id && (
                  <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
                )}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Desktop members column (step 7.2) — the resizer handle + the "People" column
 * with online/offline UserRow lists. Online/offline membership is decided by
 * which list a member is passed in; the empty state shows when both are empty.
 * The owning member memos stay in ChatLayout (they also feed the mobile sheet
 * and mention autocomplete); only this desktop JSX lives here.
 *
 * The sort toggle (Online first / Alphabetical / By role) is purely a local
 * presentation choice over the two incoming lists; the preference persists in
 * localStorage.
 */
function PeoplePanel({
  width,
  onResizeStart,
  onlineMembers,
  offlineMembers,
  onOpenProfile,
  collapsed = false,
  onToggle,
  loading = false,
}: {
  width: number;
  onResizeStart: (clientX: number) => void;
  onlineMembers: PeopleMember[];
  offlineMembers: PeopleMember[];
  onOpenProfile: (id: string) => void;
  collapsed?: boolean;
  onToggle?: () => void;
  /** Members may still be arriving: show skeleton rows instead of "No one else". */
  loading?: boolean;
}) {
  const [sortMode, setSortMode] = useState<SortMode>(() => {
    try { const v = localStorage.getItem('people.sortMode'); return (v === 'alpha' || v === 'roles') ? v : 'status'; } catch { return 'status'; }
  });
  useEffect(() => { try { localStorage.setItem('people.sortMode', sortMode); } catch {} }, [sortMode]);

  const isEmpty = onlineMembers.length === 0 && offlineMembers.length === 0;

  const combined = useMemo(
    () => [
      ...onlineMembers.map((m) => ({ m, online: true })),
      ...offlineMembers.map((m) => ({ m, online: false })),
    ],
    [onlineMembers, offlineMembers]
  );

  const alpha = useMemo(
    () => [...combined].sort((a, b) => nameOf(a.m).localeCompare(nameOf(b.m))),
    [combined]
  );

  // Group each member under their highest-ranked role (greatest position); members
  // without a role land in a trailing "No role" group. Groups are ordered by the
  // role hierarchy (highest position first).
  const roleGroups = useMemo(() => {
    const groups = new Map<string, { role: Role | null; items: { m: PeopleMember; online: boolean }[] }>();
    for (const item of combined) {
      const roles = rolesOf(item.m);
      const pr = roles.length ? roles.reduce((a, b) => ((b.position ?? 0) > (a.position ?? 0) ? b : a)) : null;
      const key = pr?.id || '__none__';
      if (!groups.has(key)) groups.set(key, { role: pr, items: [] });
      groups.get(key)!.items.push(item);
    }
    const arr = [...groups.values()];
    arr.sort((a, b) => {
      if (!a.role) return 1;
      if (!b.role) return -1;
      return (b.role.position ?? 0) - (a.role.position ?? 0) || a.role.name.localeCompare(b.role.name);
    });
    for (const g of arr) g.items.sort((x, y) => nameOf(x.m).localeCompare(nameOf(y.m)));
    return arr;
  }, [combined]);

  const renderRow = (m: PeopleMember, online: boolean) => (
    <UserRow
      key={m.id}
      data={{ id: m.id, name: m.name, username: m.username, avatarUrl: m.avatarUrl, nameColor: m.nameColor, status: (m as any).activity || undefined, rawStatus: (m as any).status, online, onMobile: m.onMobile, isIdle: m.isIdle, roles: (m as any).roles || [] }}
      onClick={() => onOpenProfile(m.id)}
    />
  );

  const GroupLabel = ({ children, color }: { children: React.ReactNode; color?: string | null }) => (
    <div className="px-2 pt-2 text-meta truncate" style={color ? { color } : undefined}>
      <span className={color ? '' : 'text-fg-3'}>{children}</span>
    </div>
  );

  if (collapsed) {
    return (
      // The whole strip is the button: a people icon with the online count on top, and a tall grab pill in the middle
      // (the same affordance as the mobile drawer tabs) that turns into an arrow on hover.
      <button
        type="button"
        onClick={onToggle}
        className="group hidden md:flex flex-col items-center bg-panel-1 order-last border-l border-neutral-800/50 text-fg-2 transition-colors hover:bg-neutral-800/50 hover:text-fg"
        style={{ width: '2.75rem' }}
        title="Show people"
        aria-label="Show members panel"
      >
        <span className="mt-3 flex flex-col items-center gap-1">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
            <circle cx="9" cy="7" r="4" />
            <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
            <path d="M16 3.13a4 4 0 0 1 0 7.75" />
          </svg>
          {onlineMembers.length > 0 && (
            <span className="flex items-center gap-1 text-[0.625rem] font-semibold leading-none text-fg-2">
              <span className="h-1.5 w-1.5 rounded-full bg-online-light" aria-hidden="true" />
              {onlineMembers.length}
            </span>
          )}
        </span>
        <span className="flex flex-1 items-center">
          <span className="relative flex h-16 w-1.5 items-center justify-center rounded-full bg-neutral-600/70 transition-all group-hover:w-6 group-hover:bg-neutral-500/80">
            <svg viewBox="0 0 24 24" className="h-4 w-4 text-white opacity-0 transition-opacity group-hover:opacity-100" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M15 18l-6-6 6-6" /></svg>
          </span>
        </span>
      </button>
    );
  }

  return (
    <>
      {/* Resizer before people column */}
      <div
        className="hidden md:block w-1 cursor-col-resize bg-transparent hover:bg-neutral-700/40 order-last"
        onMouseDown={(e) => onResizeStart(e.clientX)}
        role="separator"
        aria-label="Resize people column"
      />

      {/* People column (click to open profile/actions) */}
      <aside aria-label="People" className="hidden md:flex bg-panel-1 flex-col min-h-0 order-last" style={{ width: `clamp(10rem, 24vw, ${width}px)` }}>
        <div className="h-12 flex items-center justify-between px-3 bg-panel-0">
          <span className="font-medium text-fg">People</span>
          <div className="flex items-center gap-1">
            {!isEmpty && <SortMenu mode={sortMode} onChange={setSortMode} />}
            <button
              onClick={onToggle}
              className="flex h-7 w-7 items-center justify-center rounded-lg text-fg-2 transition hover:bg-neutral-800/60 hover:text-fg active:scale-95"
              title="Hide members"
              aria-label="Hide members panel"
            >
              <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6" /></svg>
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-auto p-2 space-y-2">
          {isEmpty && loading ? (
            <PeopleListSkeleton />
          ) : isEmpty ? (
            <div className="px-3 py-2 text-sm text-fg-3">No one else is in this channel</div>
          ) : sortMode === 'alpha' ? (
            alpha.map(({ m, online }) => renderRow(m, online))
          ) : sortMode === 'roles' ? (
            roleGroups.map((g, gi) => (
              <div key={g.role?.id || `none-${gi}`}>
                <GroupLabel color={g.role?.color}>{g.role ? g.role.name : 'No role'}</GroupLabel>
                {g.items.map(({ m, online }) => renderRow(m, online))}
              </div>
            ))
          ) : (
            <>
              {onlineMembers.map((m) => renderRow(m, true))}
              {offlineMembers.length > 0 && (
                <>
                  <GroupLabel>Offline</GroupLabel>
                  {offlineMembers.map((m) => renderRow(m, false))}
                </>
              )}
            </>
          )}
        </div>
      </aside>
    </>
  );
}

export default PeoplePanel;
